import {Button} from "./Button";
import {afterNextRender, Component, ElementRef, Injector, OnInit, signal, viewChild} from "@angular/core";
import {Wrap} from "./Wrap";
import {CustomerReview} from "./CustomerReview";
import {ReviewsService} from "../services/reviews/reviews.service";
import {Reviews} from "../model/reviews";
import {TranslateDirective, TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'CustomerReviews',
  imports: [Button, CustomerReview, TranslatePipe, TranslateDirective, Wrap],
  template: `
    @if (!loadFailed()) {
      <section class="my-section pb-6">
        <div class="pt-7 pb-10 md:pt-10 md:pb-16 xl:pt-12 relative">
          <div class="relative z-10">
            <Wrap>
              <h2 translate="CUSTOMER_REVIEWS_TITLE" class="text-[22px] md:text-4xl xl:text-[42px] leading-tight text-center mb-6 md:mb-10 xl:mb-12 px-5 md:px-10 xl:px-12"></h2>
            </Wrap>
            <div class="overflow-hidden w-full">
              <div
                #slider
                class="
                reviews-slider
                flex
                items-start
                overflow-x-auto
                no-scrollbar
                gap-[15px]
                md:gap-[30px]
                pt-2
                pb-5
                px-10 md:px-5 xl:px-10
                cursor-grab
                select-none
                min-h-[557px] md:min-h-[602px]
              "
              >
                @for (review of reviews()?.reviews ?? []; track review) {
                  <CustomerReview
                      [name]="review.author"
                      [published]="review.published"
                      [rating]="review.rating * 2"
                      [content]="review.text"
                      [url]="review.url"
                      [fixedSize]="true"
                  ></CustomerReview>
                }
              </div>
            </div>
          </div>
          <div class="absolute w-full h-full top-0 left-0 flex items-stretch px-5 md:px-10">
            <div class="w-full mx-auto max-w-[1200px] bg-yellow-100 rounded-3xl"></div>
          </div>
          <div class="absolute bottom-0 left-0 w-full flex justify-center transform translate-y-1/2">
            <Button
                color="yellow"
                href="https://www.google.com/search?q=Combidesk&ludocid=5509634814121195193#lrd=0x47b814660c4c2e05:0x4c7627ac11d1b6b9,3"
                target="_blank">
              {{ 'CUSTOMER_REVIEWS_BUTTON_GOOGLE' | translate }}
            </Button>
          </div>
        </div>
      </section>
    }
  `
})
export class CustomerReviews implements OnInit {
  readonly reviews = signal<Reviews | undefined>(undefined);

  /**
   * The section is rendered before the reviews arrive, so its height is reserved and the rest of the
   * page does not get pushed down once they do (that shift dominated our Cumulative Layout Shift).
   * The reserved height matches the measured height of a filled slider. Only when the call fails is
   * the section dropped altogether.
   */
  readonly loadFailed = signal(false);

  private readonly sliderRef = viewChild<ElementRef<HTMLElement>>('slider');

  constructor(private reviewsService: ReviewsService, private injector: Injector) {
  }

  ngOnInit(): void {
    // if this fails, we don't want to see an error in the UI
    this.reviewsService.snoozeMessages<ReviewsService>().getReviews().then(reviews => {
      this.reviews.set(reviews);
      // wait until the reviews are actually rendered before wiring up the drag behaviour
      afterNextRender(() => this.initSlider(), { injector: this.injector });
    }).catch(() => {
      this.loadFailed.set(true);
    });
  }

  getItemWidth() {
    return window.innerWidth >= 768 ? 300 : 240;
  }

  getItemGap() {
    return window.innerWidth >= 768 ? 30 : 15;
  }

  private initSlider() {
    const slider = this.sliderRef()?.nativeElement;
    if (!slider) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    slider.addEventListener('mousedown', (e: MouseEvent) => {
      isDown = true;
      slider.classList.add('cursor-grabbing');
      slider.classList.remove('cursor-grab');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider.addEventListener('mouseleave', () => {
      isDown = false;
      slider.classList.remove('cursor-grabbing');
      slider.classList.add('cursor-grab');
    });

    slider.addEventListener('mouseup', () => {
      isDown = false;
      slider.classList.remove('cursor-grabbing');
      slider.classList.add('cursor-grab');
    });

    slider.addEventListener('mousemove', (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1.5;
      slider.scrollLeft = scrollLeft - walk;
    });
  }
}
